/**
 * Due diligence projects. Each pins a company's statements from an accounting model once -
 * or starts with none, for the corporate checks alone - and is laid out on the shared
 * ProjectsPage like every discipline's list.
 */

import { useEffect, useState } from 'react';
import { useDiligenceStore } from '../../../store/diligence-projects';
import type { Discipline } from '../disciplines';
import { Button } from '../ui/Button';
import { Notice, Panel, ProjectRow, ProjectsPage } from '../ui/ProjectsPage';
import { FolderedProjects } from '../ui/ProjectFolders';

export function DiligenceProjectList({
  discipline,
  onLeaveDiscipline,
}: {
  discipline: Discipline;
  onLeaveDiscipline: () => void;
}) {
  const list = useDiligenceStore((s) => s.list);
  const models = useDiligenceStore((s) => s.models);
  const isLoading = useDiligenceStore((s) => s.isLoading);
  const isSaving = useDiligenceStore((s) => s.isSaving);
  const error = useDiligenceStore((s) => s.error);
  const clearError = useDiligenceStore((s) => s.clearError);
  const listProjects = useDiligenceStore((s) => s.listProjects);
  const listModels = useDiligenceStore((s) => s.listModels);
  const examples = useDiligenceStore((s) => s.examples);
  const listExamples = useDiligenceStore((s) => s.listExamples);
  const openExample = useDiligenceStore((s) => s.openExample);
  const create = useDiligenceStore((s) => s.create);
  const open = useDiligenceStore((s) => s.open);
  const remove = useDiligenceStore((s) => s.remove);
  const [name, setName] = useState('');
  const [modelId, setModelId] = useState('');

  useEffect(() => {
    void listProjects();
    void listModels();
    void listExamples();
  }, [listProjects, listModels, listExamples]);

  return (
    <ProjectsPage
      discipline={discipline}
      onLeaveDiscipline={onLeaveDiscipline}
      intro="Preliminary findings from a company's statements: net debt, working capital, cash conversion, quality of earnings and red flags - each with its source and its arithmetic."
    >
      {error && <Notice onDismiss={clearError}>{error}</Notice>}
      <div className="mt-8 grid gap-4">
        <Panel
          title="Start a diligence project"
          description="Link an accounting project: its statements are pinned now and never change behind you - refreshing is a step you take, shown what moved first."
        >
          <form
            className="flex flex-wrap gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (name.trim()) void create(name.trim(), modelId || null);
            }}
          >
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Project name"
              aria-label="Project name"
              className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            />
            <select
              value={modelId}
              onChange={(event) => setModelId(event.target.value)}
              aria-label="Accounting project to link"
              className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
            >
              <option value="">No statements (corporate checks only)</option>
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
            <Button type="submit" variant="primary" disabled={!name.trim() || isSaving}>
              Start
            </Button>
          </form>
        </Panel>
      </div>
      <FolderedProjects
        discipline={discipline.id}
        onCopied={() => void listProjects()}
        isLoading={isLoading}
        empty="No diligence projects yet. Start one above, or open a sample."
        samples={examples.map((example) => ({ ...example, onOpen: () => void openExample(example.key) }))}
        projects={list.map((row) => ({
          id: row.id,
          render: (folderControl, options) => (
          <ProjectRow
            key={row.id}
            folderControl={folderControl}
            title={row.name}
            meta={
              row.sourceKind === 'linked'
                ? `Pinned from ${row.modelName ?? 'an accounting project'}${row.pinnedAt ? ` on ${row.pinnedAt.slice(0, 10)}` : ''} · ${row.years.join(', ')}`
                : row.sourceKind === 'example'
                  ? `Sample - fictitious figures · ${row.years.join(', ')}`
                  : 'No statements linked'
            }
            note={row.high ? `${row.high} high-severity finding${row.high === 1 ? '' : 's'}` : undefined}
            aside={<span className="shrink-0 text-xs text-gray-500">{row.findings} findings</span>}
            onOpen={() => void open(row.id)}
            onDelete={options?.sample ? undefined : () => void remove(row.id)}
          />
          ),
        }))}
      />
    </ProjectsPage>
  );
}
